/**
 * Purchase Section Manager
 * Handles quantity syncing, cart updates, and purchase section functionality
 */

class PurchaseSection {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.selectors = {
      quantityInput: `#Quantity-${sectionId}`,
      purchaseQuantityInput: `#purchase-quantity-${sectionId}`,
      purchaseForm: `#purchase-form-${sectionId}`,
      cartQuantitySpan: '.quantity__rules-cart span:last-child',
      cartQuantityContainer: '.quantity__rules-cart',
      quantityWrapper: '.quantity, .product-form__quantity'
    };
    
    this.elements = {};
    this.currentVariantId = null;
    
    this.init();
  }

  /**
   * Initialize the purchase section
   */
  init() {
    console.log(`[Purchase Section] Initializing for section: ${this.sectionId}`);
    this.cacheElements();
    this.logElementStatus();
    this.bindEvents();
    this.setupCartUpdateHooks();
    
    if (this.elements.quantityInput && this.elements.purchaseQuantityInput) {
      this.syncQuantityInitial();
      console.log(`[Purchase Section] Quantity syncing enabled`);
    } else {
      console.warn(`[Purchase Section] Missing elements - quantity syncing disabled`);
    }
  }

  /**
   * Cache DOM elements for better performance
   */
  cacheElements() {
    Object.keys(this.selectors).forEach(key => {
      this.elements[key] = document.querySelector(this.selectors[key]);
    });
    
    // Cache the quantity wrapper
    if (this.elements.quantityInput) {
      this.elements.quantityWrapper = this.elements.quantityInput.closest(this.selectors.quantityWrapper);
    }
  }

  /**
   * Log element status for debugging
   */
  logElementStatus() {
    console.log(`[Purchase Section] Element status:`, {
      quantityInput: !!this.elements.quantityInput,
      purchaseQuantityInput: !!this.elements.purchaseQuantityInput,
      purchaseForm: !!this.elements.purchaseForm,
      cartQuantitySpan: !!this.elements.cartQuantitySpan,
      quantityWrapper: !!this.elements.quantityWrapper
    });
    
    if (!this.elements.quantityInput) {
      console.warn(`[Purchase Section] Could not find quantity input: ${this.selectors.quantityInput}`);
    }
    if (!this.elements.purchaseQuantityInput) {
      console.warn(`[Purchase Section] Could not find purchase quantity input: ${this.selectors.purchaseQuantityInput}`);
    }
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    if (this.elements.quantityInput && this.elements.purchaseQuantityInput) {
      // Listen for direct input changes
      this.elements.quantityInput.addEventListener('input', () => this.syncQuantity());
      this.elements.quantityInput.addEventListener('change', () => this.syncQuantity());
      
      // Listen for button clicks (+ and - buttons)
      if (this.elements.quantityWrapper) {
        this.elements.quantityWrapper.addEventListener('click', () => {
          setTimeout(() => this.syncQuantity(), 10);
        });
      }
    }
  }

  /**
   * Set up cart update detection
   */
  setupCartUpdateHooks() {
    // Listen for various cart update events
    const updateEvents = ['cart:updated', 'cart:added', 'cart:changed'];
    updateEvents.forEach(event => {
      document.addEventListener(event, () => this.updateCartQuantityDisplay());
    });

    // Hook into PubSub system if available
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => this.updateCartQuantityDisplay());
    }

    // Monitor fetch requests to cart endpoints
    this.interceptCartRequests();
  }

  /**
   * Intercept fetch requests to cart endpoints
   */
  interceptCartRequests() {
    if (window.fetch.__purchaseSectionIntercepted) return;
    
    const originalFetch = window.fetch;
    window.fetch = (...args) => {
      const url = args[0];
      const isCartRequest = typeof url === 'string' && 
        (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update'));
      
      if (isCartRequest) {
        return originalFetch.apply(window, args).then(response => {
          // Delay update to ensure cart state is synchronized
          setTimeout(() => this.updateCartQuantityDisplay(), 500);
          return response;
        });
      }
      
      return originalFetch.apply(window, args);
    };
    
    window.fetch.__purchaseSectionIntercepted = true;
  }

  /**
   * Sync quantity from main selector to purchase form
   */
  syncQuantity() {
    if (this.elements.quantityInput && this.elements.purchaseQuantityInput) {
      const newValue = this.elements.quantityInput.value;
      this.elements.purchaseQuantityInput.value = newValue;
      console.log(`[Purchase Section] Synced quantity: ${newValue}`);
    } else {
      console.warn(`[Purchase Section] Cannot sync quantity - missing elements`);
    }
  }

  /**
   * Initial quantity sync
   */
  syncQuantityInitial() {
    this.syncQuantity();
  }

  /**
   * Update the cart quantity display in the quantity selector
   */
  async updateCartQuantityDisplay() {
    try {
      const cart = await this.fetchCart();
      const currentCartQuantity = this.getVariantQuantityInCart(cart);
      
      console.log(`[Purchase Section] Updating cart quantity display: ${currentCartQuantity}`);
      
      this.updateQuantityIndicator(currentCartQuantity);
    } catch (error) {
      console.error('[Purchase Section] Error updating cart quantity:', error);
    }
  }

  /**
   * Fetch current cart state
   */
  async fetchCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error(`Cart fetch failed: ${response.status}`);
    }
    return response.json();
  }

  /**
   * Get the quantity of current variant in cart
   */
  getVariantQuantityInCart(cart) {
    if (!this.currentVariantId) {
      // Extract variant ID from page context or form
      const variantInput = document.querySelector('.product-variant-id');
      this.currentVariantId = variantInput ? variantInput.value : null;
    }
    
    if (!this.currentVariantId) return 0;
    
    const cartItem = cart.items.find(item => 
      item.variant_id.toString() === this.currentVariantId.toString()
    );
    
    return cartItem ? cartItem.quantity : 0;
  }

  /**
   * Update the quantity indicator in the UI
   */
  updateQuantityIndicator(quantity) {
    if (!this.elements.cartQuantitySpan) return;

    const container = this.elements.cartQuantitySpan.closest(this.selectors.cartQuantityContainer);
    
    if (quantity > 0) {
      if (container) {
        container.classList.remove('hidden');
      }
      this.elements.cartQuantitySpan.textContent = `(${quantity} in cart)`;
    } else {
      if (container) {
        container.classList.add('hidden');
      }
    }
  }

  /**
   * Set the current variant ID (call this when variant changes)
   */
  setVariantId(variantId) {
    this.currentVariantId = variantId;
    this.updateCartQuantityDisplay();
  }

  /**
   * Refresh all functionality (useful for dynamic content)
   */
  refresh() {
    this.cacheElements();
    this.updateCartQuantityDisplay();
  }

  /**
   * Destroy the instance and clean up
   */
  destroy() {
    // Remove event listeners
    Object.keys(this.elements).forEach(key => {
      const element = this.elements[key];
      if (element && element.removeEventListener) {
        element.removeEventListener('input', this.syncQuantity);
        element.removeEventListener('change', this.syncQuantity);
        element.removeEventListener('click', this.syncQuantity);
      }
    });
    
    this.elements = {};
  }
}

/**
 * Auto-initialize purchase sections when DOM is ready
 */
document.addEventListener('DOMContentLoaded', () => {
  console.log('[Purchase Section] DOM loaded, initializing...');
  
  // Wait a bit for all elements to be rendered
  setTimeout(() => {
    // Look for purchase sections and initialize them
    const purchaseForms = document.querySelectorAll('[id^="purchase-form-"]');
    console.log(`[Purchase Section] Found ${purchaseForms.length} purchase forms`);
    
    purchaseForms.forEach(form => {
      const sectionId = form.id.replace('purchase-form-', '');
      console.log(`[Purchase Section] Initializing section: ${sectionId}`);
      
      // Create global reference for debugging and external access
      window[`purchaseSection_${sectionId}`] = new PurchaseSection(sectionId);
    });
    
    // If no forms found, try a broader search
    if (purchaseForms.length === 0) {
      console.warn('[Purchase Section] No purchase forms found, trying alternative detection...');
      
      // Look for any product forms and try to extract section ID
      const productForms = document.querySelectorAll('product-form[data-section-id]');
      productForms.forEach(form => {
        const sectionId = form.getAttribute('data-section-id');
        if (sectionId && !window[`purchaseSection_${sectionId}`]) {
          console.log(`[Purchase Section] Found product form for section: ${sectionId}`);
          window[`purchaseSection_${sectionId}`] = new PurchaseSection(sectionId);
        }
      });
    }
  }, 100);
});

/**
 * Utility function to get purchase section instance
 */
window.getPurchaseSection = function(sectionId) {
  return window[`purchaseSection_${sectionId}`];
};

/**
 * Export for module systems (if needed)
 */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = PurchaseSection;
}